'use client';

import { useEffect, useState } from 'react';

import { Breadcrumb } from '@/components/Breadcrumb/breadcrumb';
import { ListingStructuredData } from '@/features/seo-listing/components/listing-structured-data';
import { SeoListingHeader } from '@/features/seo-listing/components/seo-listing-header';
import { SeoListingText } from '@/features/seo-listing/components/seo-listing-text';
import { useFandomCharactersCount } from '@/features/seo-listing/hooks/use-fandom-characters-count';
import type { SeoResolveData } from '@/features/seo-listing/types';
import type { ListingStructuredWork } from '@/features/seo-listing/utils/build-listing-structured-data';
import {
  buildListingPaginationHref,
  LISTING_PAGE_SUFFIX,
} from '@/features/seo-listing/utils/parse-listing-page-param';
import {
  buildFandomCharactersHref,
  buildFandomPermalinkHref,
  buildListingBottomSeoHeading,
  buildListingBreadcrumbs,
  buildListingDocumentTitle,
  getFandomSlugFromPath,
  getFandomSlugFromPermanentUrl,
  getListingChromeKind,
  resolveListingHeading,
  stripFandomPrefixFromPageName,
  type ListingBreadcrumbSection,
} from '@/features/seo-listing/utils/seo-listing';
import {
  isSeoH1CompatibleWithChromeKind,
  readFandomIdFromSeoPage,
  resolveListingChromeKind,
  resolveListingFandomTitle,
} from '@/features/seo-listing/utils/resolve-listing-chrome';
import {
  createCatalogNavigateHandler,
  dispatchListingFandomHrefNavigate,
} from '@/features/fanfic-card/consts/LISTING_FANDOM_NAVIGATE_EVENT';
import { FANDOM_PERMALINK_PATTERN } from '@/features/seo-listing/consts/SEO_ROUTE_PATTERNS';

type SeoListingChromeProps = {
  pathname: string;
  seoPage: SeoResolveData | null;
  listingWorksCount: number | null;
  fandomsCount?: number | null;
  selectedFandoms: string[];
  /** When SEO redirect is missing (legacy GET), chrome kind is inferred from these. */
  selectedCharacters?: string[];
  fandomTitle?: string;
  /**
   * Selected fandom permalink from filter options (`/fanfics/fandoms/{slug}`).
   * Needed on legacy `/fanfics?fandoms=` when address bar / seoPage have no slug yet —
   * otherwise the «N персонажей» pill stays a non-clickable span.
   */
  fandomPermanentUrl?: string | null;
  entityName?: string;
  /** While SEO redirect/resolve is in flight, keep the previous H1 to avoid template flash. */
  isSeoPending?: boolean;
  section?: ListingBreadcrumbSection;
  /** Listing page number for H1 suffix «— страница N» when ≥2. */
  listingPage?: number;
  /** Query preserved on the crawlable listing URL, without `page`. */
  listingSearchParams?: Record<string, string | string[] | undefined>;
  /** Works currently rendered as cards, in that order. */
  shownWorks?: readonly ListingStructuredWork[];
};

export function SeoListingChrome({
  pathname,
  seoPage,
  listingWorksCount,
  fandomsCount,
  selectedFandoms,
  selectedCharacters = [],
  fandomTitle,
  fandomPermanentUrl,
  entityName,
  isSeoPending = false,
  section = 'fanfics',
  listingPage = 1,
  listingSearchParams = {},
  shownWorks = [],
}: SeoListingChromeProps) {
  // Prefer address-bar path after soft SEO pushState; Next pathname can lag.
  // Do not prefer seoPage.permanentUrl — it stays on the previous permalink after
  // browser Back until catalog SEO is restored, which froze the H1 on fandom copy.
  const listingPathname = typeof window !== 'undefined' ? window.location.pathname : pathname;
  const kind = resolveListingChromeKind({
    pathname: listingPathname,
    pageType: seoPage?.pageType,
    selectedFandomsCount: selectedFandoms.length,
    selectedCharactersCount: selectedCharacters.length,
  });
  const fandomSlug =
    getFandomSlugFromPath(listingPathname) ??
    getFandomSlugFromPermanentUrl(seoPage?.permanentUrl) ??
    getFandomSlugFromPermanentUrl(fandomPermanentUrl);
  const pageName = seoPage?.pageName?.trim() || '';
  const seoPageType = (seoPage?.pageType ?? null) as string | null;
  // Never prefer catalog listing pageName ("Фанфики") over filter fandom title —
  // that created «Фэндом «Фанфики»» / «Главная / Фанфики / Фанфики» on reload
  // when fandom permalink SEO is disabled and SSR still passes catalog resolve.
  const resolvedFandomTitle = resolveListingFandomTitle({
    kind,
    pageName,
    pageType: seoPageType,
    fandomTitle,
  });
  // Prefer filter-provided entityName. SEO pageName is only safe when resolve
  // metadata matches the chrome kind (character select under a fandom permalink
  // often leaves seoPage on the fandom — its pageName must not become the entity).
  const seoPageNameUsableAsEntity =
    (kind === 'fandom_character' &&
      (seoPageType === 'character' || seoPageType === 'fandom_character')) ||
    (kind === 'fandom_pairing' &&
      (seoPageType === 'pairing' || seoPageType === 'fandom_pairing')) ||
    (kind === 'tag' && seoPageType === 'tag');
  const resolvedEntityName = stripFandomPrefixFromPageName(
    kind === 'fandom_character' || kind === 'fandom_pairing' || kind === 'tag'
      ? entityName?.trim() || (seoPageNameUsableAsEntity ? pageName : '')
      : kind === 'catalog'
        ? entityName?.trim() || ''
        : entityName?.trim() || '',
    resolvedFandomTitle,
  );
  // Ignore SEO H1 when resolve metadata is for a different page than chrome kind
  // (e.g. catalog listing h1 on legacy ?fandoms=&characters=, or fandom h1 after
  // character select while resolve is still catching up).
  const seoH1Usable =
    Boolean(seoPage?.h1?.trim()) && isSeoH1CompatibleWithChromeKind(kind, seoPageType);
  const nextH1Base = resolveListingHeading({
    kind,
    resolvedH1: seoH1Usable ? seoPage?.h1 : null,
    fandomTitle: resolvedFandomTitle,
    entityName: resolvedEntityName,
    section,
  });
  const nextH1 = `${nextH1Base}${LISTING_PAGE_SUFFIX(listingPage)}`;
  const [committedH1, setCommittedH1] = useState(nextH1);
  if (!isSeoPending && committedH1 !== nextH1) {
    setCommittedH1(nextH1);
  }
  const h1 = isSeoPending ? committedH1 : nextH1;
  useEffect(() => {
    const seoTitle = seoPage?.title?.trim() ?? '';
    if (seoTitle.length > 0) return;
    document.title = buildListingDocumentTitle(h1);
  }, [h1, seoPage?.title]);
  const worksCount =
    kind === 'fandom_characters' ? null : (listingWorksCount ?? seoPage?.worksCount ?? null);
  const fandomHref = fandomSlug ? buildFandomPermalinkHref(fandomSlug) : undefined;
  const selectedFandomId =
    readFandomIdFromSeoPage(seoPage) ??
    (selectedFandoms.length === 1 ? selectedFandoms[0] : undefined);
  // Figma «Страница персонажа»: only «N работ в фэндоме» — no characters pill.
  const shouldShowCharactersCount =
    kind === 'fandom' || (kind === 'catalog' && Boolean(selectedFandomId));
  const charactersCount = useFandomCharactersCount(
    shouldShowCharactersCount ? selectedFandomId : undefined,
  );
  const breadcrumbs = buildListingBreadcrumbs({
    kind,
    section,
    fandomTitle: resolvedFandomTitle,
    fandomHref,
    entityName: resolvedEntityName,
  });
  const currentPath = buildListingPaginationHref(listingPathname, listingSearchParams, listingPage);
  const handleBreadcrumbNavigate = (href: string, event: { preventDefault: () => void }) => {
    const path = href.split('?')[0] ?? href;
    if (path === '/fanfics' || path === '/originals') {
      createCatalogNavigateHandler(path)(event);
      return;
    }
    if (FANDOM_PERMALINK_PATTERN.test(path)) {
      dispatchListingFandomHrefNavigate(path, selectedFandomId, event.preventDefault);
    }
  };

  return (
    <div className="mb-6">
      <ListingStructuredData
        breadcrumbs={breadcrumbs}
        heading={h1}
        currentPath={currentPath}
        works={shownWorks}
      />
      <div className="mb-3">
        <Breadcrumb items={breadcrumbs} variant="light" onItemNavigate={handleBreadcrumbNavigate} />
      </div>
      <SeoListingHeader
        h1={h1}
        worksCount={worksCount}
        charactersCount={shouldShowCharactersCount ? charactersCount : null}
        charactersHref={
          shouldShowCharactersCount && fandomSlug
            ? buildFandomCharactersHref(fandomSlug)
            : undefined
        }
        charactersCountSuffix={kind === 'fandom_characters' ? 'фэндома' : undefined}
        worksCountSuffix={
          kind === 'fandom' || kind === 'fandom_character' || kind === 'fandom_pairing'
            ? 'в фэндоме'
            : undefined
        }
        fandomsCount={kind === 'catalog' ? fandomsCount : null}
      />
      {seoPage?.seoTextTop ? (
        <div className="mb-4">
          <SeoListingText text={seoPage.seoTextTop} variant="top" />
        </div>
      ) : null}
    </div>
  );
}

export function SeoListingFooter({
  pathname,
  seoPage,
  fandomTitle,
}: Pick<SeoListingChromeProps, 'pathname' | 'seoPage' | 'fandomTitle'>) {
  const listingPathname = typeof window !== 'undefined' ? window.location.pathname : pathname;
  const kind = getListingChromeKind(listingPathname);
  const bottomSeo = buildListingBottomSeoHeading({
    kind,
    fandomTitle: fandomTitle?.trim() || seoPage?.pageName,
  });
  if (!seoPage?.seoTextBottom) return null;
  return (
    <div className="mt-10">
      <SeoListingText
        text={seoPage.seoTextBottom}
        variant="bottom"
        eyebrow={bottomSeo?.eyebrow}
        heading={bottomSeo?.heading}
      />
    </div>
  );
}
